import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth';
import { CaseHeader } from '../CaseHeader';
import { CaseSidebar } from '../CaseSidebar';
import { ActivityFilters } from '../ActivityFilters';
import { ActivityFeed } from '../ActivityFeed';
import { InlineQuickComposer } from '../InlineQuickComposer';
import { TrackerAttachmentsTab } from '../TrackerAttachmentsTab';
import { ChannelNotificationSettings } from '../ChannelNotificationSettings';
import { ChangeResponsableModal } from '../ChangeResponsableModal';
import { ResponsableHistory } from '../ResponsableHistory';
import { InlineStatusForm } from '../InlineStatusForm';
import { trackerAPI } from '../../api/tracker.api';
import { searchAPI } from '../../../search/api/search.api';
import { EmailContentModal } from '../../../emails/components/EmailContentModal';
import { EmailReallocationModal } from '../EmailReallocationModal';
import { SmsReallocationModal } from '../SmsReallocationModal';
import type { Affaire } from '../../../search/types/search.types';
import { useChannelSubscription } from '../../hooks/useChannelSubscription';
import { useChannelNotifications } from '../../hooks/useChannelNotifications';
import { useAffaireSettings } from '../../hooks/useAffaireSettings';
import { useAffaireActiveStatus } from '../../hooks/useAffaireActiveStatus';
import { useChronologicCommunications } from '../../hooks/useChronologicCommunications';
import { useTrackerEmails } from '../../hooks/useTrackerEmails';
import { useJustifyingItems } from '../../hooks/useJustifyingItems';
import { useAffairePaymentSummary } from '../../hooks/useAffairePaymentSummary';
import { mergeAndSortActivities } from '../../utils/activityAdapters';
import { exportTrackerPdf } from '../../utils/exportTrackerPdf';
import type { TrackerChannel, CommunicationType, TrackerEmail, AffaireContact } from '../../types/tracker.types';
import type { ActionType, OverflowActionType, AssigneeData } from '../CaseHeader/CaseHeader.types';
import type { ActivityItemViewModel } from '../ActivityItem/ActivityItem.types';
import { UserIcon } from '@heroicons/react/24/outline';
import '../../styles/todo.css';
import './TrackerDetailPage.styles.css';

/**
 * TrackerDetailPage - Redesigned detail view for a specific tracker channel
 * Features:
 * - Sticky header with key info and actions
 * - 2-column layout (activity feed + sidebar)
 * - Modern chip-based filters
 * - Mobile-responsive with sidebar drawer
 */
export const TrackerDetailPage: React.FC = () => {
  const { affaireId } = useParams<{ affaireId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Core state
  const [channel, setChannel] = useState<TrackerChannel | null>(null);
  const [channelLoading, setChannelLoading] = useState(true);
  const [channelError, setChannelError] = useState<string | null>(null);

  // Full affaire data (from searchAPI)
  const [affaire, setAffaire] = useState<Affaire | null>(null);
  const [affaireLoading, setAffaireLoading] = useState(true);

  // Affaire contacts
  const [contacts, setContacts] = useState<AffaireContact[]>([]);

  // Export PDF (frise client) — génération en cours (appel IA)
  const [exportingPdf, setExportingPdf] = useState(false);

  // Modal states
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showChangeResponsable, setShowChangeResponsable] = useState(false);
  const [selectedResponsableLine, setSelectedResponsableLine] = useState<'primary' | 'second' | 'third'>('primary');
  const [showResponsableHistory, setShowResponsableHistory] = useState(false);

  // Email modal states
  const [viewEmailModal, setViewEmailModal] = useState<TrackerEmail | null>(null);
  const [reallocateEmail, setReallocateEmail] = useState<TrackerEmail | null>(null);
  const [replyEmail, setReplyEmail] = useState<any | null>(null);

  // SMS reallocation state
  const [reallocateSmsId, setReallocateSmsId] = useState<string | null>(null);

  // New layout states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'activity' | 'documents'>('activity');

  // Inline form states
  const [showStatusForm, setShowStatusForm] = useState(false);

  // Edit/Delete tracker_com state
  const [editingTrackerCom, setEditingTrackerCom] = useState<{ id: string; content: string; urgency: 'low' | 'medium' | 'high' | 'urgent'; assignedTo: string } | null>(null);
  const [editEligibleUsers, setEditEligibleUsers] = useState<{ id: string; first_name: string; last_name: string; email: string; role: string; fm_name: string }[]>([]);
  const [isLoadingEditUsers, setIsLoadingEditUsers] = useState(false);

  // Fetch eligible users when edit modal opens
  useEffect(() => {
    if (!editingTrackerCom) return;
    const fetchUsers = async () => {
      setIsLoadingEditUsers(true);
      try {
        const response: any = await trackerAPI.getEligibleResponsables();
        if (response.success) {
          setEditEligibleUsers(response.data);
        }
      } catch (error) {
        console.error('Error fetching eligible users:', error);
      } finally {
        setIsLoadingEditUsers(false);
      }
    };
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!editingTrackerCom]);

  // Selected user for edit modal avatar
  const editSelectedUser = useMemo(() => {
    return editEligibleUsers.find(u => u.id === editingTrackerCom?.assignedTo);
  }, [editEligibleUsers, editingTrackerCom?.assignedTo]);

  // Note assignee from sidebar intervenant click
  const [noteAssignee, setNoteAssignee] = useState<string | undefined>();
  const composerRef = useRef<HTMLDivElement>(null);

  // Filter states
  const [enabledTypes, setEnabledTypes] = useState<Set<CommunicationType>>(
    new Set(['status_update', 'email', 'sms_outbox', 'sms_inbox', 'com_avant_envoyer', 'com_post_it', 'com_internes', 'tracker_com', 'rdv', 'voip_call'])
  );

  // All available types
  const allTypes: CommunicationType[] = ['status_update', 'email', 'sms_outbox', 'sms_inbox', 'com_avant_envoyer', 'com_post_it', 'com_internes', 'tracker_com', 'rdv', 'voip_call'];

  // Parse affaireId
  const affaireIdNum = affaireId ? parseInt(affaireId, 10) : null;

  // Fetch affaire settings and responsables
  const { settings: affaireSettings, isLoading: settingsLoading, refetch: refetchSettings } = useAffaireSettings(affaireIdNum);

  // Subscription management
  const { subscribe, unsubscribe, updateNotifications, isLoading: subscriptionLoading } = useChannelSubscription();

  // Get affaire active status to check if frozen
  const {
    status: affaireStatus,
    isLoading: affaireStatusLoading,
    clearManualInactive,
    refetch: refetchStatus
  } = useAffaireActiveStatus(affaireIdNum || 0);

  // Fetch communications
  const { communications, isLoading: communicationsLoading } = useChronologicCommunications(affaireIdNum || 0);

  // Fetch emails
  const { data: emailsData, isLoading: emailsLoading } = useTrackerEmails(affaireIdNum || 0);
  const emails = emailsData?.data || [];

  // Fetch justifying items
  const { highlightedComIds } = useJustifyingItems(affaireIdNum || 0);

  // Fetch payment summary for sidebar
  const { data: paymentSummary } = useAffairePaymentSummary(affaireIdNum);

  // Check permissions
  const canChangeResponsable = Boolean(user && (user.role === 'bureau' || user.role === 'admin'));
  const canManageStatus = user?.role === 'admin' || user?.role === 'bureau';

  // Check if affaire is frozen
  const isFrozen = !affaireStatusLoading && affaireStatus && !affaireStatus.is_active &&
    (affaireStatus.inactive_reason === 'manual_temporary' ||
     affaireStatus.inactive_reason === 'manual_until_activity' ||
     affaireStatus.inactive_reason === 'status_archived');

  // Build freeze info text
  const freezeInfo = useMemo(() => {
    if (!affaireStatus) return '';

    if (affaireStatus.inactive_reason === 'status_archived') {
      return 'Gelé automatiquement (dossier archivé)';
    }

    if (affaireStatus.inactive_reason === 'manual_until_activity') {
      return "En pause jusqu'à prochaine activité";
    }

    if (affaireStatus.inactive_reason === 'manual_temporary' && affaireStatus.inactive_until) {
      const freezeDate = new Date(affaireStatus.inactive_until);
      const now = new Date();
      const freezeDateOnly = new Date(freezeDate.getFullYear(), freezeDate.getMonth(), freezeDate.getDate());
      const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffTime = freezeDateOnly.getTime() - nowDateOnly.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return "Expirée (en attente de reactivation)";
      } else if (diffDays === 0) {
        return "Gelée jusqu'à aujourd'hui";
      } else if (diffDays === 1) {
        return "Gelée pour encore 1 jour";
      } else {
        return `Gelée pour ${diffDays} jours`;
      }
    }

    return '';
  }, [affaireStatus]);

  // Build assignee data from affaire settings
  const assignee = useMemo<AssigneeData | undefined>(() => {
    if (!affaireSettings?.primary_responsable?.id) return undefined;

    const resp = affaireSettings.primary_responsable;
    const name = `${resp.first_name || ''} ${resp.last_name || ''}`.trim() || resp.email || 'Utilisateur';
    const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);

    return {
      id: resp.id,
      name,
      initials,
    };
  }, [affaireSettings]);

  // Transform communications to activity view models (including synthetic RDV card)
  const activities = useMemo<ActivityItemViewModel[]>(() => {
    const baseActivities = mergeAndSortActivities(communications, emails, 'desc');

    // Create synthetic RDV activity if date_du_rv exists
    if (affaire?.date_du_rv) {
      // Parse date and time
      const dateStr = affaire.date_du_rv;
      const timeStr = affaire.heure_rv || '09:00';

      // Create a Date object for the RDV
      let rdvDate: Date;
      try {
        // Try to parse ISO date format (e.g., "2025-01-15")
        const [year, month, day] = dateStr.split('-').map(Number);
        const [hours, minutes] = timeStr.split(':').map(Number);
        rdvDate = new Date(year, month - 1, day, hours || 9, minutes || 0);
      } catch {
        rdvDate = new Date(dateStr);
      }

      // Only add if valid date
      if (!isNaN(rdvDate.getTime())) {
        const rdvActivity: ActivityItemViewModel = {
          type: 'rdv',
          id: `rdv-${affaire.id_unique}`,
          title: 'Rendez-vous expertise',
          meta: affaire.expert ? `Expert: ${affaire.expert}` : undefined,
          snippet: affaire.address
            ? `${affaire.address.number || ''} ${affaire.address.street || ''}, ${affaire.address.postcode || ''} ${affaire.address.locality || ''}`.trim().replace(/\s+/g, ' ')
            : undefined,
          timestamp: rdvDate,
          urgency: null,
          isDone: false,
        };

        // Insert RDV in the correct chronological position
        const allActivities = [...baseActivities, rdvActivity];
        allActivities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        return allActivities;
      }
    }

    return baseActivities;
  }, [communications, emails, affaire]);

  // Calculate type counts
  const typeCounts = useMemo(() => {
    const counts: Record<CommunicationType | 'sms', number> = {
      email: 0,
      sms_outbox: 0,
      sms_inbox: 0,
      sms: 0,
      status_update: 0,
      com_post_it: 0,
      com_internes: 0,
      tracker_com: 0,
      com_avant_envoyer: 0,
      rdv: 0,
    };

    activities.forEach(activity => {
      if (activity.type in counts) {
        counts[activity.type]++;
      }
      // Count SMS combined
      if (activity.type === 'sms_outbox' || activity.type === 'sms_inbox') {
        counts.sms++;
      }
    });

    return counts;
  }, [activities]);

  // Fetch channel data on mount
  useEffect(() => {
    const fetchChannel = async () => {
      if (!affaireId) {
        setChannelError('No affaire ID provided');
        setChannelLoading(false);
        return;
      }

      try {
        setChannelLoading(true);
        setChannelError(null);
        const response = await trackerAPI.getChannelByAffaire(parseInt(affaireId, 10));

        if (response.success) {
          setChannel(response.data);
        } else {
          setChannelError('Failed to load channel');
        }
      } catch (error) {
        console.error('Error fetching channel:', error);
        setChannelError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setChannelLoading(false);
      }
    };

    fetchChannel();
  }, [affaireId]);

  // Fetch full affaire data from searchAPI (for status, amount, etc.)
  useEffect(() => {
    const fetchAffaire = async () => {
      if (!affaireId) {
        setAffaireLoading(false);
        return;
      }

      try {
        setAffaireLoading(true);
        const response = await searchAPI.searchAffaire(affaireId);

        if (response.success && response.data.affaire) {
          setAffaire(response.data.affaire);
        }
      } catch (error) {
        console.error('Error fetching affaire:', error);
      } finally {
        setAffaireLoading(false);
      }
    };

    fetchAffaire();
  }, [affaireId]);

  // Fetch contacts for sidebar
  useEffect(() => {
    const fetchContacts = async () => {
      if (!affaireIdNum) return;

      try {
        const response = await trackerAPI.getAffaireContacts(affaireIdNum);
        if (response.success) {
          setContacts(response.data.contacts);
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
      }
    };

    fetchContacts();
  }, [affaireIdNum]);

  // Track if we've already marked the affaire as opened
  const hasMarkedOpened = useRef(false);

  // Automatically mark communications as opened when user views this affaire
  useEffect(() => {
    if (!affaireId || hasMarkedOpened.current) return;

    const markOpened = async () => {
      try {
        hasMarkedOpened.current = true;
        await trackerAPI.markAffaireOpened(parseInt(affaireId, 10));
      } catch (error) {
        console.error('Error marking affaire as opened:', error);
      }
    };

    markOpened();
  }, [affaireId]);

  // Real-time notifications for this channel
  useChannelNotifications({
    channelId: channel?.id || null,
    onTaskCreated: () => {
      if (affaireId) {
        trackerAPI.getChannelByAffaire(parseInt(affaireId, 10))
          .then(response => {
            if (response.success) {
              setChannel(response.data);
            }
          });
      }
    },
    onTaskUpdated: () => {
      if (affaireId) {
        trackerAPI.getChannelByAffaire(parseInt(affaireId, 10))
          .then(response => {
            if (response.success) {
              setChannel(response.data);
            }
          });
      }
    },
    onCommunicationCreated: () => {
      if (affaireIdNum) {
        queryClient.invalidateQueries({
          queryKey: ['chronologic-communications', affaireIdNum]
        });
      }
    },
    onCommunicationUpdated: () => {
      if (affaireIdNum) {
        queryClient.invalidateQueries({
          queryKey: ['chronologic-communications', affaireIdNum]
        });
      }
    }
  });

  // Handle subscription
  const handleSubscribe = async () => {
    if (!channel) return;
    const success = await subscribe(channel.id);
    if (success && affaireId) {
      queryClient.invalidateQueries({ queryKey: ['tracker-channels-all'] });
      const response = await trackerAPI.getChannelByAffaire(parseInt(affaireId, 10));
      if (response.success) {
        setChannel(response.data);
      }
    }
  };

  const handleUnsubscribe = async () => {
    if (!channel) return;
    const result = await unsubscribe(channel.id);

    if (result.requiresConfirmation) {
      const confirmed = window.confirm(
        result.message ||
          'Vous êtes automatiquement abonné à ce canal car vous êtes responsable de cette affaire.\n\n' +
          'Êtes-vous sûr de vouloir vous désabonner ?'
      );

      if (confirmed) {
        const forceResult = await unsubscribe(channel.id, true);
        if (forceResult.success && affaireId) {
          queryClient.invalidateQueries({ queryKey: ['tracker-channels-all'] });
          const response = await trackerAPI.getChannelByAffaire(parseInt(affaireId, 10));
          if (response.success) {
            setChannel(response.data);
          }
        }
      }
    } else if (result.success && affaireId) {
      queryClient.invalidateQueries({ queryKey: ['tracker-channels-all'] });
      const response = await trackerAPI.getChannelByAffaire(parseInt(affaireId, 10));
      if (response.success) {
        setChannel(response.data);
      }
    }
  };

  // Toggle subscription (for both dropdown action and sidebar shortcut)
  const handleToggleSubscription = async () => {
    if (channel?.is_subscribed) {
      await handleUnsubscribe();
    } else {
      await handleSubscribe();
    }
  };

  const handleSaveNotificationSettings = async (settings: {
    notifications_enabled: boolean;
    notification_settings: any;
  }) => {
    if (!channel) return;
    const success = await updateNotifications(channel.id, settings);
    if (success && affaireId) {
      const response = await trackerAPI.getChannelByAffaire(parseInt(affaireId, 10));
      if (response.success) {
        setChannel(response.data);
      }
      setShowNotificationSettings(false);
    }
  };

  // Handle responsable change success
  const handleResponsableChangeSuccess = () => {
    refetchSettings();
  };

  // Handle refresh after todo/message success
  const handleTodoSuccess = async () => {
    if (affaireIdNum) {
      queryClient.invalidateQueries({
        queryKey: ['chronologic-communications', affaireIdNum]
      });

      const response = await trackerAPI.getChannelByAffaire(affaireIdNum);
      if (response.success) {
        setChannel(response.data);
      }
    }
  };

  // Handle reactivate
  const handleReactivate = async () => {
    try {
      await clearManualInactive();
      await refetchStatus();
      handleTodoSuccess();
    } catch (error) {
      console.error('Error reactivating affaire:', error);
    }
  };

  // Handle back button
  const handleBack = () => {
    const fromExpertiseDetail = location.state?.fromExpertiseDetail;
    const fromAgentLea = location.state?.fromAgentLea;
    const fromPath = location.state?.from;

    if (fromAgentLea && fromPath) {
      navigate(fromPath, {
        state: {
          agentLeaPage: location.state?.agentLeaPage,
          agentLeaScroll: location.state?.agentLeaScroll,
        },
      });
      return;
    }

    if (fromExpertiseDetail && fromPath) {
      navigate(fromPath);
      return;
    }

    const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';

    if (fromPath?.includes('/pipeline')) {
      navigate(`${basePath}/pipeline`);
    } else if (fromPath?.includes('/payment')) {
      navigate(`${basePath}/payment`, {
        state: { scrollPosition: location.state?.scrollPosition }
      });
    } else {
      navigate(`${basePath}/tracker`, {
        state: {
          lastClickedAffaireId: affaireId ? parseInt(affaireId, 10) : undefined
        }
      });
    }
  };

  // Handle FileMaker button click
  const handleFileMaker = () => {
    if (affaireId) {
      const fileMakerUrl = `fmp://$/fm2.chatelainxpert.be/ChatelainXpert.fmp12?script=mondaytofm&param=${affaireId}`;
      window.location.href = fileMakerUrl;
    }
  };

  // Handle expertise detail navigation
  const handleExpertiseDetail = () => {
    if (affaireId) {
      const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
      navigate(`${basePath}/mes-dossiers/${affaireId}`, {
        state: { from: location.pathname }
      });
    }
  };

  // Handle Facturation button click
  const handleFacturation = () => {
    if (affaireId) {
      const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
      navigate(`${basePath}/facturation`, {
        state: {
          preselectedAffaireId: parseInt(affaireId, 10),
          from: location.pathname
        }
      });
    }
  };

  // Handle action from CaseHeader dropdown
  const handleAction = (action: ActionType) => {
    switch (action) {
      case 'status':
        setShowStatusForm(true);
        break;
      case 'email':
        // TODO: Open email compose modal
        console.log('Email action');
        break;
      case 'postit':
        // TODO: Open post-it modal
        console.log('Post-it action');
        break;
      case 'document':
        // TODO: Open document upload modal
        console.log('Document action');
        break;
      case 'invoice':
        handleFacturation();
        break;
      case 'task':
        // TODO: Open task creation modal
        console.log('Task action');
        break;
      case 'subscribe':
        handleToggleSubscription();
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Handle overflow action from CaseHeader
  const handleOverflowAction = (action: OverflowActionType) => {
    switch (action) {
      case 'billing':
        handleFacturation();
        break;
      case 'filemaker':
        handleFileMaker();
        break;
      case 'expertise-detail':
        handleExpertiseDetail();
        break;
      case 'copy-link':
        if (affaireId) {
          const url = `${window.location.origin}/admin/tracker/${affaireId}`;
          navigator.clipboard.writeText(url);
        }
        break;
      case 'history':
        setShowResponsableHistory(true);
        break;
      default:
        console.log('Unknown overflow action:', action);
    }
  };

  // Filter handlers
  const handleToggleType = (type: CommunicationType) => {
    setEnabledTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const handleShowAllTypes = () => {
    setEnabledTypes(new Set(allTypes));
  };

  // Select only a specific type (exclusive mode)
  const handleSelectOnly = (type: CommunicationType) => {
    // For SMS, select both inbox and outbox
    if (type === 'sms_outbox' || type === 'sms_inbox') {
      setEnabledTypes(new Set(['sms_outbox', 'sms_inbox']));
    } else {
      setEnabledTypes(new Set([type]));
    }
  };

  // Check if filters are active
  const hasActiveFilters = enabledTypes.size !== allTypes.length;

  // Reset filters
  const handleResetFilters = () => {
    setEnabledTypes(new Set(allTypes));
  };


  // Activity callbacks - signatures match ActivityFeed expectations
  const handleMarkDone = async (type: CommunicationType, id: string, done: boolean, comment?: string) => {
    try {
      if (type === 'tracker_com') {
        await trackerAPI.markTrackerComDone(id, done, comment);
      } else if (type === 'com_internes') {
        await trackerAPI.markCommunicationDone('com_internes', id, done, comment);
      }

      if (affaireIdNum) {
        await queryClient.refetchQueries({
          queryKey: ['chronologic-communications', affaireIdNum],
          type: 'active'
        });
      }

      handleTodoSuccess();
    } catch (error) {
      console.error('Error marking as done:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  // Handle view email in modal
  const handleViewEmailFull = (emailId: string) => {
    const email = emails.find(e => e.id === emailId);
    if (email) {
      setViewEmailModal(email);
    }
  };

  // Handle reply to email
  const handleReplyEmail = (emailId: string) => {
    const email = emails.find(e => e.id === emailId);
    if (email) {
      setReplyEmail({
        id: email.id,
        sender_address: email.sender_address,
        sender_name: email.sender_name,
        subject: email.subject,
        full_content: email.full_content || email.body_preview || '',
        received_time: email.received_time,
      });
      // Scroll to composer
      composerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle email reallocation
  const handleReallocateEmail = (emailId: string) => {
    const email = emails.find(e => e.id === emailId);
    if (email) {
      setReallocateEmail(email);
    }
  };

  // Handle reallocation success
  const handleReallocationSuccess = () => {
    setReallocateEmail(null);
    if (affaireIdNum) {
      queryClient.invalidateQueries({
        queryKey: ['tracker-emails', affaireIdNum]
      });
    }
    handleTodoSuccess();
  };

  // Handle SMS reallocation
  const handleReallocateSms = (smsId: string) => {
    setReallocateSmsId(smsId);
  };

  // Handle SMS reallocation success
  const handleSmsReallocationSuccess = () => {
    setReallocateSmsId(null);
    if (affaireIdNum) {
      queryClient.invalidateQueries({
        queryKey: ['chronologic-communications', affaireIdNum]
      });
    }
    handleTodoSuccess();
  };

  // Handle edit tracker_com message
  const handleEditTrackerCom = (id: string, content: string, urgency?: string | null, assignedTo?: string | null) => {
    setEditingTrackerCom({
      id,
      content,
      urgency: (urgency as 'low' | 'medium' | 'high' | 'urgent') || 'medium',
      assignedTo: assignedTo || '',
    });
  };

  // Save edited tracker_com message
  const handleSaveEditedTrackerCom = async () => {
    if (!editingTrackerCom || !editingTrackerCom.content.trim()) return;

    try {
      // Update message content
      await trackerAPI.updateTrackerCom(editingTrackerCom.id, editingTrackerCom.content.trim());

      // Update metadata (urgency + assignment) — upsert via POST
      await trackerAPI.createTrackerComMetadata(editingTrackerCom.id, {
        urgency: editingTrackerCom.urgency,
        assigned_to: editingTrackerCom.assignedTo || null,
      });

      // Refetch communications
      if (affaireIdNum) {
        await queryClient.refetchQueries({
          queryKey: ['chronologic-communications', affaireIdNum],
          type: 'active'
        });
      }

      handleTodoSuccess();

      // Close modal and reset state
      setEditingTrackerCom(null);
    } catch (error) {
      console.error('Error updating message:', error);
      alert('Erreur lors de la modification du message');
    }
  };

  // Handle delete tracker_com message
  const handleDeleteTrackerCom = async (id: string) => {
    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?');
    if (!confirmed) return;

    try {
      await trackerAPI.deleteTrackerCom(id);

      // Refetch communications
      if (affaireIdNum) {
        await queryClient.refetchQueries({
          queryKey: ['chronologic-communications', affaireIdNum],
          type: 'active'
        });
      }

      handleTodoSuccess();
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Erreur lors de la suppression du message');
    }
  };

  // Loading state
  if (channelLoading) {
    return (
      <div className="tracker-detail-page">
        <div className="tracker-detail-loading">
          <div className="loading-spinner"></div>
          <p>Chargement du channel...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (channelError || !channel) {
    return (
      <div className="tracker-detail-page">
        <div className="tracker-detail-error">
          <h2>Erreur</h2>
          <p>{channelError || 'Channel non trouvé'}</p>
          <button onClick={handleBack} className="btn-back">
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  // Build address string for header using TrackerChannel properties
  const addressString = [
    channel.numero_rue,
    channel.adresse_rue_bien,
    channel.commune_bien,
  ].filter(Boolean).join(' ');

  // Get last activity date
  const lastActivityDate = activities.length > 0 ? activities[0].timestamp : undefined;

  // Export « frise du dossier » orienté client : la curation (jalons +
  // échanges clés, sans bruit interne) + l'enrichissement IA des
  // points-clés sont faits dans exportTrackerPdf, donc on lui passe
  // TOUTES les activités, indépendamment des filtres d'affichage.
  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      // Date (+ heure) du RV d'expertise → jalon « visite » de la frise.
      let visitDate: Date | null = null;
      if (affaire?.date_du_rv) {
        const d = new Date(affaire.date_du_rv);
        if (!Number.isNaN(d.getTime())) {
          const m = /^(\d{1,2}):(\d{2})/.exec(affaire.heure_rv || '');
          if (m) d.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
          visitDate = d;
        }
      }
      await exportTrackerPdf({
        affaireId: channel.affaire_id,
        address: addressString || undefined,
        status: affaire?.statut_affaire || channel.status || undefined,
        missionType: affaire?.type_mission,
        amount: affaire?.montant_tvac_affaire,
        visitDate,
        activities,
      });
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="tracker-detail-page redesigned">
      {/* Sticky Header */}
      <CaseHeader
        affaireId={channel.affaire_id}
        status={affaire?.statut_affaire || channel.status || 'N/A'}
        missionType={affaire?.type_mission}
        address={addressString || undefined}
        amount={affaire?.montant_tvac_affaire}
        lastActivityDate={lastActivityDate}
        assignee={assignee}
        affaireSettings={affaireSettings}
        settingsLoading={settingsLoading}
        canChangeResponsable={canChangeResponsable}
        isFrozen={isFrozen || false}
        freezeInfo={freezeInfo}
        onBack={handleBack}
        onAction={handleAction}
        onOverflowAction={handleOverflowAction}
        onChangeAssignee={() => {
          setSelectedResponsableLine('primary');
          setShowChangeResponsable(true);
        }}
        onReactivate={canManageStatus && isFrozen ? handleReactivate : undefined}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        sidebarOpen={sidebarOpen}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isSubscribed={channel.is_subscribed}
      />

      {/* Main content layout */}
      <div className="tracker-detail-layout">
        {/* Main content */}
        <main className="tracker-detail-main">
          {/* Inline Status Form */}
          {showStatusForm && (
            <InlineStatusForm
              affaireId={channel.affaire_id}
              onClose={() => setShowStatusForm(false)}
              onSuccess={() => {
                refetchStatus();
                handleTodoSuccess();
                setShowStatusForm(false);
              }}
            />
          )}

          {/* Conditional content based on active tab */}
          {activeTab === 'activity' ? (
            <>
              {/* Activity Filters */}
              <ActivityFilters
                enabledTypes={enabledTypes}
                onToggleType={handleToggleType}
                onShowAll={handleShowAllTypes}
                onSelectOnly={handleSelectOnly}
                typeCounts={typeCounts}
                totalCount={activities.length}
                hasActiveFilters={hasActiveFilters}
                onReset={handleResetFilters}
                onExportPdf={handleExportPdf}
                exportingPdf={exportingPdf}
              />

              {/* Inline Quick Composer (Note/SMS) */}
              <div ref={composerRef}>
                <InlineQuickComposer
                  affaireId={channel.affaire_id}
                  onSuccess={() => {
                    setNoteAssignee(undefined);
                    setReplyEmail(null);
                    handleTodoSuccess();
                  }}
                  affaireDetails={{
                    reference: String(channel.affaire_id),
                    address: addressString || undefined,
                    expert: affaire?.expert || undefined,
                    expert_phone: affaire?.expert_phone || undefined,
                    date_rv: affaire?.date_du_rv || undefined,
                    heure_rv: affaire?.heure_rv || undefined,
                    montant: affaire?.montant_tvac_affaire || undefined,
                  }}
                  defaultAssignedTo={noteAssignee}
                  replyTo={replyEmail}
                />
              </div>

              {/* Activity Feed */}
              <ActivityFeed
                activities={activities}
                isLoading={communicationsLoading || emailsLoading}
                enabledTypes={enabledTypes}
                currentUserId={user?.id}
                onMarkDone={handleMarkDone}
                onEdit={handleEditTrackerCom}
                onDelete={handleDeleteTrackerCom}
                onViewEmailFull={handleViewEmailFull}
                onReallocateEmail={handleReallocateEmail}
                onReallocateSms={handleReallocateSms}
                onReplyEmail={handleReplyEmail}
              />
            </>
          ) : (
            /* Documents Tab */
            <TrackerAttachmentsTab
              emails={emails}
              isLoading={emailsLoading}
              affaireId={affaireIdNum || undefined}
            />
          )}
        </main>

        {/* Sidebar */}
        <CaseSidebar
          affaireId={channel.affaire_id}
          status={affaire?.statut_affaire || channel.status || 'N/A'}
          amount={affaire?.montant_tvac_affaire}
          lastActivityDate={lastActivityDate}
          contacts={contacts}
          client={affaire?.client ? {
            name: `${affaire.client.firstName || ''} ${affaire.client.lastName || ''}`.trim() || 'Client',
            email: affaire.client.email,
            phone: affaire.client.phone || affaire.client.phoneMobile,
          } : undefined}
          address={addressString ? {
            street: channel.adresse_rue_bien || undefined,
            number: channel.numero_rue || undefined,
            city: channel.commune_bien || undefined,
          } : undefined}
          expert={affaire?.expert}
          dates={affaire ? {
            dateIn: affaire.date_demande,
            dateRv: affaire.date_du_rv,
            dateSortie: affaire.date_sortie,
            encodeur: affaire.encodeur,
            encodeurUserId: affaire.encodeur_user_id,
            rvPrisPar: affaire.rv_pris_par,
            rvPrisParUserId: affaire.rv_pris_par_user_id,
            eval1: affaire.eval1 || undefined,
            eval2: affaire.eval2 || undefined,
            eval3: affaire.eval3 || undefined,
          } : undefined}
          affaireSettings={affaireSettings}
          finance={paymentSummary ? {
            totalHonorairesTvac: paymentSummary.billing.totalTvac,
            expertHonorairesTvac: paymentSummary.billing.expertHonorairesTvac,
            paymentStatus: paymentSummary.payments.paymentStatus,
            totalPaid: paymentSummary.payments.totalPaid,
            totalInvoiced: paymentSummary.invoicing.totalInvoicedTvac,
            isDirectPayment: paymentSummary.payments.isDirectPayment,
            productCount: paymentSummary.billing.productCount,
          } : undefined}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onFileMaker={handleFileMaker}
          onBilling={handleFacturation}
          onViewHistory={() => setShowResponsableHistory(true)}
          onChangeResponsable={(line) => {
            setSelectedResponsableLine(line);
            setShowChangeResponsable(true);
          }}
          canChangeResponsable={canChangeResponsable}
          isSubscribed={channel.is_subscribed}
          onToggleSubscription={handleToggleSubscription}
          subscriptionLoading={subscriptionLoading}
          onOpenNote={(userId) => {
            setNoteAssignee(userId);
            composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
        />
      </div>

      {/* Modals */}
      <ChannelNotificationSettings
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
        channel={channel}
        onSave={handleSaveNotificationSettings}
      />

      {affaireSettings && (
        <ChangeResponsableModal
          isOpen={showChangeResponsable}
          onClose={() => setShowChangeResponsable(false)}
          affaireId={channel.affaire_id}
          currentSettings={affaireSettings}
          responsableLine={selectedResponsableLine}
          onSuccess={handleResponsableChangeSuccess}
        />
      )}

      <ResponsableHistory
        isOpen={showResponsableHistory}
        onClose={() => setShowResponsableHistory(false)}
        affaireId={channel.affaire_id}
      />

      {/* Email Content Modal */}
      {viewEmailModal && (
        <EmailContentModal
          email={viewEmailModal}
          onClose={() => setViewEmailModal(null)}
          currentAffaireId={channel.affaire_id}
          onReallocate={(email) => {
            setViewEmailModal(null);
            setReallocateEmail(email);
          }}
        />
      )}

      {/* Email Reallocation Modal */}
      {reallocateEmail && (
        <EmailReallocationModal
          email={reallocateEmail}
          currentAffaireId={channel.affaire_id}
          onClose={() => setReallocateEmail(null)}
          onSuccess={handleReallocationSuccess}
        />
      )}

      {/* SMS Reallocation Modal */}
      {reallocateSmsId && (
        <SmsReallocationModal
          smsId={reallocateSmsId}
          currentAffaireId={channel.affaire_id}
          onClose={() => setReallocateSmsId(null)}
          onSuccess={handleSmsReallocationSuccess}
        />
      )}

      {/* Edit Tracker Com Modal */}
      {editingTrackerCom && (
        <div className="tracker-edit-modal-overlay" onClick={() => setEditingTrackerCom(null)}>
          <div className="tracker-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tracker-edit-modal-header">
              <h3>Modifier le message</h3>
              <button
                className="tracker-edit-modal-close"
                onClick={() => setEditingTrackerCom(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="tracker-edit-modal-body">
              <textarea
                className="tracker-edit-textarea"
                value={editingTrackerCom.content}
                onChange={(e) => setEditingTrackerCom({ ...editingTrackerCom, content: e.target.value })}
                rows={6}
                autoFocus
              />

              {/* Urgency selector */}
              <div className="todo-priority-section" style={{ marginTop: '12px' }}>
                <label className="todo-form__label">Urgence</label>
                <div
                  className="todo-priority-toggle"
                  data-active-index={(['low', 'medium', 'high', 'urgent'] as const).indexOf(editingTrackerCom.urgency)}
                  style={{ '--item-count': '4' } as React.CSSProperties}
                >
                  {([
                    { value: 'low' as const, label: 'Basse' },
                    { value: 'medium' as const, label: 'Moyenne' },
                    { value: 'high' as const, label: 'Haute' },
                    { value: 'urgent' as const, label: 'Urgente' },
                  ]).map((u) => (
                    <button
                      key={u.value}
                      type="button"
                      onClick={() => setEditingTrackerCom({ ...editingTrackerCom, urgency: u.value })}
                      className={`todo-priority-option ${editingTrackerCom.urgency === u.value ? 'active' : ''}`}
                      data-priority={u.value}
                      style={{ minWidth: '80px' }}
                    >
                      <span className={`todo-priority-dot todo-priority-dot--${u.value}`} />
                      {u.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assigned-to selector */}
              <div className="todo-form__field" style={{ marginTop: '12px' }}>
                <label htmlFor="edit-assigned-user" className="todo-form__label">
                  Attribuer à
                </label>
                {isLoadingEditUsers ? (
                  <div className="todo-form__loading">Chargement des utilisateurs...</div>
                ) : (
                  <div className="todo-user-select-wrapper">
                    <div className={`todo-user-avatar ${!editSelectedUser ? 'todo-user-avatar--empty' : ''}`}>
                      {editSelectedUser ? (
                        `${editSelectedUser.first_name.charAt(0)}${editSelectedUser.last_name.charAt(0)}`.toUpperCase()
                      ) : (
                        <UserIcon className="w-4 h-4" />
                      )}
                    </div>
                    <select
                      id="edit-assigned-user"
                      value={editingTrackerCom.assignedTo}
                      onChange={(e) => setEditingTrackerCom({ ...editingTrackerCom, assignedTo: e.target.value })}
                      className="todo-user-select"
                    >
                      <option value="">Non attribué</option>
                      {editEligibleUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.role})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="tracker-edit-modal-footer">
              <button
                className="tracker-edit-btn-cancel"
                onClick={() => setEditingTrackerCom(null)}
              >
                Annuler
              </button>
              <button
                className="tracker-edit-btn-save"
                onClick={handleSaveEditedTrackerCom}
                disabled={!editingTrackerCom.content.trim()}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
